import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { publicApi } from "../../lib/publicApi";
import usePublicSettings from "../../lib/usePublicSettings";
import VehicleCard from "../../components/VehicleCard";
import { useT, lbl } from "../../lib/i18n";

import { tr } from "../../lib/i18n";
const SORTS = [
  { v: "newest", k: "sort_newest" },
  { v: "price_asc", k: "sort_price_asc" },
  { v: "price_desc", k: "sort_price_desc" },
  { v: "mileage_asc", k: "sort_mileage" },
];

export default function ListingPage() {
  const { t } = useT();
  const { settings } = usePublicSettings();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ makes: [], body_types: [], transmissions: [], fuel_types: [], colors: [], specifications: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const filters = useMemo(() => Object.fromEntries([...params.entries()].filter(([k]) => k !== "preview_theme")), [params]);

  useEffect(() => {
    publicApi.get("/facets").then((r) => setFacets(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    publicApi
      .get("/vehicles", { params: { ...filters, page, limit: 12 } })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filters, page]);

  const setF = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value == null) next.delete(key);
    else next.set(key, value);
    setPage(1);
    setParams(next);
  };
  const clear = () => setParams({});
  const [open, setOpen] = useState(false);
  const active = Object.entries(filters).filter(([k, v]) => k !== "sort" && k !== "page" && k !== "preview_theme" && v !== "" && v != null).length;

  return (
    <div data-testid="listing-page">
      <div className="vd-band on-dark">
        <div className="public-container" style={{ padding: "140px 20px 48px" }}>
          <div className="kicker" style={{ color: "rgba(255,255,255,0.6)" }}>{t("nav.cars")}</div>
          <h1 style={{ fontSize: "clamp(44px,5.6vw,88px)", fontWeight: 200, letterSpacing: "-0.03em", margin: "14px 0 0", color: "#fff" }}>
            <span style={{ fontWeight: 600 }}>{total}</span> {tr("x.cars")} <em style={{ fontStyle: "normal", opacity: 0.75 }}>{tr("x.ready_today")}</em>
          </h1>
        </div>
      </div>
      <div className="public-container" style={{ padding: "40px 20px 80px" }}>
      <div style={{ display: "grid", gap: 32, gridTemplateColumns: "260px 1fr" }} className="listing-grid">
        <aside className={`filters${open ? " open" : ""}`} data-testid="filters" role={open ? "dialog" : undefined} aria-modal={open || undefined}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <h3>{t("listing.filters")}</h3>
            <button className="chip" onClick={clear} data-testid="filters-clear">{t("listing.clear")}</button>
            <button className="chip filters-close" onClick={() => setOpen(false)} data-testid="filters-close">{tr("x.close")}</button>
          </div>

          <div className="filter-group">
            <label>{t("listing.make")}</label>
            <select className="filter-select" value={filters.make || ""} onChange={(e) => setF("make", e.target.value)} data-testid="filter-make">
              <option value="">{tr("x.all")}</option>
              {facets.makes?.map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t("listing.body_type")}</label>
            <select className="filter-select" value={filters.body_type || ""} onChange={(e) => setF("body_type", e.target.value)} data-testid="filter-body-type">
              <option value="">{tr("x.all")}</option>
              {facets.body_types?.map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t("listing.transmission")}</label>
            <select className="filter-select" value={filters.transmission || ""} onChange={(e) => setF("transmission", e.target.value)} data-testid="filter-transmission">
              <option value="">{tr("x.all")}</option>
              {facets.transmissions?.map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t("listing.fuel_type")}</label>
            <select className="filter-select" value={filters.fuel_type || ""} onChange={(e) => setF("fuel_type", e.target.value)} data-testid="filter-fuel">
              <option value="">{tr("x.all")}</option>
              {facets.fuel_types?.map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t("listing.year_range")}</label>
            <div className="range-row">
              <input className="filter-input" type="number" placeholder={tr("x.min")} value={filters.year_min || ""} onChange={(e) => setF("year_min", e.target.value)} data-testid="filter-year-min" />
              <input className="filter-input" type="number" placeholder={tr("x.max")} value={filters.year_max || ""} onChange={(e) => setF("year_max", e.target.value)} data-testid="filter-year-max" />
            </div>
          </div>
          <div className="filter-group">
            <label>{t("listing.price_range")}</label>
            <div className="range-row">
              <input className="filter-input" type="number" placeholder={tr("x.min")} value={filters.price_min || ""} onChange={(e) => setF("price_min", e.target.value)} data-testid="filter-price-min" />
              <input className="filter-input" type="number" placeholder={tr("x.max")} value={filters.price_max || ""} onChange={(e) => setF("price_max", e.target.value)} data-testid="filter-price-max" />
            </div>
          </div>
          <div className="filter-group">
            <label>{t("listing.mileage_max")}</label>
            <input className="filter-input" type="number" placeholder={tr("x.km_unit")} value={filters.mileage_max || ""} onChange={(e) => setF("mileage_max", e.target.value)} data-testid="filter-mileage-max" />
          </div>
          <div className="filter-group">
            <label>{t("listing.specification")}</label>
            <select className="filter-select" value={filters.specification || ""} onChange={(e) => setF("specification", e.target.value)} data-testid="filter-specification">
              <option value="">{tr("x.all")}</option>
              {facets.specifications?.map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
            </select>
          </div>
          <button className="pub-cta primary full filters-done" onClick={() => setOpen(false)} data-testid="filters-done">
            {t("listing.showing", { count: items.length, total })}
          </button>
        </aside>
        {open && <div className="filters-backdrop" onClick={() => setOpen(false)} data-testid="filters-backdrop" />}

        <section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
            <button className="chip filters-toggle" onClick={() => setOpen(true)} aria-expanded={open} data-testid="filters-toggle">
              {t("listing.filters")}{active ? ` (${active})` : ""}
            </button>
            <span style={{ color: "var(--pub-muted)", fontSize: 14 }} data-testid="results-count">
              {t("listing.showing", { count: items.length, total })}
            </span>
            <select
              className="filter-select"
              style={{ width: "auto" }}
              value={filters.sort || "newest"}
              onChange={(e) => setF("sort", e.target.value)}
              data-testid="sort-select"
            >
              {SORTS.map((s) => <option key={s.v} value={s.v}>{t(`listing.${s.k /* i18n-keys: sort_newest,sort_price_asc,sort_price_desc,sort_mileage */}`)}</option>)}
            </select>
          </div>

          {loading ? (
            <p style={{ color: "var(--pub-muted)" }}>{tr("x.loading")}</p>
          ) : items.length === 0 ? (
            <div className="filters" style={{ position: "static", textAlign: "center", padding: 48 }} data-testid="no-results">
              {t("listing.no_results")}
            </div>
          ) : (
            <div className="vgrid">
              {items.map((v) => <VehicleCard key={v.stock_number} v={v} settings={settings} />)}
            </div>
          )}

          {total > 12 ? (
            <div style={{ display: "flex", gap: 8, marginTop: 32, justifyContent: "center" }}>
              <button className="chip" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev">
                {tr("x.previous")}
              </button>
              <span className="chip">{tr("x.page")} {page} {tr("x.of")} {Math.ceil(total / 12)}</span>
              <button className="chip" onClick={() => setPage((p) => p + 1)} disabled={page * 12 >= total} data-testid="page-next">
                {tr("x.next")}
              </button>
            </div>
          ) : null}
        </section>
      </div>
      </div>

      <style>{`.filters-toggle, .filters-close, .filters-done { display: none; }
        @media (max-width: 1023px) {
          .listing-grid { grid-template-columns: 1fr !important; }
          .filters { display: none; }
          .filters.open { display: flex; align-self: stretch; height: 100dvh; position: fixed !important; top: 0; bottom: 0; inset-inline-end: 0; width: min(380px, 100%); z-index: 80; overflow-y: auto; border-radius: 0; }
          .filters-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 79; }
          .filters-toggle, .filters-close { display: inline-flex; }
          .filters-done { display: block; width: 100%; margin-top: 20px; }
        }`}</style>
    </div>
  );
}
